import { useState, useEffect } from 'react'
import { AlarmClock, Calendar, RefreshCw, Loader2, ChevronLeft, ChevronRight, ChevronDown, LogIn, LogOut } from 'lucide-react'
import { rrGetAdminAttendance, rrGetPunchLogs } from '@/actions/rr'

interface AgentRow {
  user_id: number; name: string
  status: 'present' | 'absent' | 'punched_out'
  first_punch_in: string | null
  last_punch_in: string | null
  last_punch_out: string | null
  total_hours: number | null; total_hours_fmt: string
  on_break: boolean
}

interface PunchLogEntry {
  user_id: number; name: string
  event_type: 'punch_in' | 'punch_out'
  event_at: string; reason: string | null
}

const AV_COLORS = ['#2f9e6b','#3b82c4','#b9772e','#8a5cd1','#c2557a','#2aa1a8']
function avColor(n: string) { let h = 0; for (const c of n) h = (h * 31 + c.charCodeAt(0)) % AV_COLORS.length; return AV_COLORS[h] }
function initials(n: string) { const p = n.trim().split(' '); return ((p[0]?.[0]??'')+(p[1]?.[0]??'')).toUpperCase() }

function StatusPill({ status, onBreak }: { status: string; onBreak: boolean }) {
  if (status === 'present' && onBreak)
    return <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />On Break</span>
  if (status === 'present')
    return <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />Present</span>
  if (status === 'punched_out')
    return <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-slate-400" />Punched Out</span>
  return <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Absent</span>
}

function dateToISO(d: Date) { return d.toISOString().slice(0, 10) }
function offsetDate(iso: string, days: number) {
  const d = new Date(iso); d.setDate(d.getDate() + days); return dateToISO(d)
}
function formatDisplay(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
}

function PunchTimeline({ logs }: { logs: PunchLogEntry[] }) {
  if (!logs.length) return <div className="text-xs text-gray-400 py-2 px-1">No punch log entries yet today.</div>

  const sessions: { in: PunchLogEntry; out?: PunchLogEntry }[] = []
  let i = 0
  while (i < logs.length) {
    if (logs[i].event_type === 'punch_in') {
      const session: { in: PunchLogEntry; out?: PunchLogEntry } = { in: logs[i] }
      if (i + 1 < logs.length && logs[i + 1].event_type === 'punch_out') {
        session.out = logs[i + 1]; i += 2
      } else { i++ }
      sessions.push(session)
    } else { i++ }
  }

  return (
    <div className="flex flex-col gap-2 py-2 px-1">
      {sessions.map((s, idx) => {
        let duration = ''
        if (s.out) {
          const [ih, im] = s.in.event_at.split(':').map(Number)
          const [oh, om] = s.out.event_at.split(':').map(Number)
          const diff = (oh * 60 + om) - (ih * 60 + im)
          if (diff > 0) duration = diff >= 60 ? `${Math.floor(diff/60)}h ${diff%60}m` : `${diff}m`
        }
        return (
          <div key={idx} className="flex items-center gap-3 text-xs">
            <span className="text-gray-400 w-5 text-right font-semibold">{idx + 1}.</span>
            <span className="flex items-center gap-1 text-green-700 font-semibold w-16">
              <LogIn className="w-3 h-3 flex-shrink-0" />{s.in.event_at.slice(0, 5)}
            </span>
            <span className="text-gray-300">→</span>
            {s.out
              ? <span className="flex items-center gap-1 text-slate-600 font-semibold w-16"><LogOut className="w-3 h-3 flex-shrink-0" />{s.out.event_at.slice(0, 5)}</span>
              : <span className="text-green-500 italic w-16">active</span>
            }
            {duration && <span className="text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">{duration}</span>}
          </div>
        )
      })}
    </div>
  )
}

export default function AttendancePage() {
  const [dateStr, setDateStr] = useState(dateToISO(new Date()))
  const [agents, setAgents] = useState<AgentRow[]>([])
  const [summary, setSummary] = useState({ present: 0, absent: 0, date: '' })
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [logs, setLogs] = useState<Record<number, PunchLogEntry[]>>({})
  const [logsLoading, setLogsLoading] = useState<number | null>(null)

  function load(d: string) {
    setLoading(true)
    setExpandedId(null)
    setLogs({})
    rrGetAdminAttendance(d).then(res => {
      const d2 = res?.data as { agents?: AgentRow[]; present_count?: number; absent_count?: number; date?: string } ?? {}
      setAgents((d2.agents ?? []) as AgentRow[])
      setSummary({ present: d2.present_count ?? 0, absent: d2.absent_count ?? 0, date: d2.date ?? '' })
    }).finally(() => setLoading(false))
  }

  useEffect(() => { load(dateStr) }, [dateStr])

  async function toggleExpand(userId: number) {
    if (expandedId === userId) { setExpandedId(null); return }
    setExpandedId(userId)
    if (logs[userId]) return
    setLogsLoading(userId)
    try {
      const res = await rrGetPunchLogs(dateStr, userId)
      const entries = (res?.data as { logs?: PunchLogEntry[] })?.logs ?? []
      setLogs(prev => ({ ...prev, [userId]: entries }))
    } finally { setLogsLoading(null) }
  }

  const isToday = dateStr === dateToISO(new Date())
  const sorted = [...agents].sort((a, b) => {
    const score = (s: string) => s === 'present' ? 0 : s === 'punched_out' ? 1 : 2
    const d = score(a.status) - score(b.status)
    return d !== 0 ? d : a.name.localeCompare(b.name)
  })

  return (
    <div className="h-full min-h-0 flex flex-col p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brandColor flex items-center justify-center flex-shrink-0">
            <AlarmClock className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-gray-900">Attendance</h1>
            <p className="text-sm text-gray-500 mt-0.5">Daily agent punch-in / punch-out log. Click any row to see full timeline.</p>
          </div>
        </div>
        <button onClick={() => load(dateStr)} className="inline-flex items-center gap-2 h-9 px-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Date nav */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => setDateStr(offsetDate(dateStr, -1))} className="w-9 h-9 border border-gray-300 rounded-lg flex items-center justify-center text-gray-600 hover:bg-gray-50">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 h-9 border border-gray-300 rounded-lg px-3 bg-white">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input type="date" value={dateStr} max={dateToISO(new Date())} onChange={e => setDateStr(e.target.value)}
            className="text-sm font-semibold text-gray-900 border-0 outline-none bg-transparent" />
        </div>
        <button onClick={() => setDateStr(offsetDate(dateStr, 1))} disabled={isToday}
          className="w-9 h-9 border border-gray-300 rounded-lg flex items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-30">
          <ChevronRight className="w-4 h-4" />
        </button>
        <span className="text-sm text-gray-500">{formatDisplay(dateStr)}</span>
        {isToday && <span className="text-xs font-bold text-brandColor bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">Today</span>}
      </div>

      {/* Summary strip */}
      {!loading && (
        <div className="flex gap-3 mb-4">
          {[
            { label: 'Present',     value: summary.present,                                       cls: 'bg-green-50 border-green-200 text-green-700' },
            { label: 'Punched Out', value: agents.filter(a => a.status === 'punched_out').length,  cls: 'bg-slate-50 border-slate-200 text-slate-600' },
            { label: 'Absent',      value: summary.absent,                                        cls: 'bg-gray-50  border-gray-200  text-gray-500'  },
            { label: 'Total',       value: agents.length,                                         cls: 'bg-white    border-gray-200  text-gray-700'  },
          ].map(s => (
            <div key={s.label} className={`flex items-center gap-2.5 px-4 py-2.5 border rounded-xl ${s.cls}`}>
              <span className="text-2xl font-extrabold">{s.value}</span>
              <span className="text-xs font-bold uppercase tracking-wider opacity-70">{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex-1 min-h-0 flex flex-col">
        {loading
          ? <div className="flex justify-center items-center h-48"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>
          : agents.length === 0
            ? <div className="flex justify-center items-center h-32 text-gray-400 text-sm">No agents found</div>
            : <div className="overflow-y-auto flex-1">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 z-10 bg-gray-50">
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-5 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-400">Agent</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-400">Status</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-400">First In</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-400">Last In</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-400">Last Out</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-400">Hours</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {sorted.map(a => (
                    <>
                      <tr
                        key={a.user_id}
                        onClick={() => a.status !== 'absent' && toggleExpand(a.user_id)}
                        className={`${a.status === 'absent' ? 'opacity-50' : 'cursor-pointer hover:bg-gray-50/60'}`}
                      >
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2.5">
                            <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0" style={{ background: avColor(a.name) }}>{initials(a.name)}</span>
                            <span className="text-sm font-semibold text-gray-900">{a.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3"><StatusPill status={a.status} onBreak={a.on_break} /></td>
                        <td className="px-4 py-3 text-sm text-gray-700 font-medium">{a.first_punch_in ?? '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 font-medium">{a.last_punch_in ?? '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 font-medium">{a.last_punch_out ?? '—'}</td>
                        <td className="px-4 py-3 text-sm font-bold text-gray-900">{a.total_hours_fmt}</td>
                        <td className="px-2 py-3">
                          {a.status !== 'absent' && (
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedId === a.user_id ? 'rotate-180' : ''}`} />
                          )}
                        </td>
                      </tr>

                      {/* Timeline row */}
                      {expandedId === a.user_id && (
                        <tr key={`${a.user_id}-log`} className="bg-gray-50/50">
                          <td colSpan={7} className="px-16 pb-3 pt-1 border-t border-gray-100">
                            {logsLoading === a.user_id
                              ? <div className="flex items-center gap-2 py-2 text-xs text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin" />Loading…</div>
                              : <PunchTimeline logs={logs[a.user_id] ?? []} />
                            }
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
        }
      </div>
    </div>
  )
}
